import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { useNavigate } from 'react-router';
import {
  getAccessStatusForVisitorGroup,
  getAllLockGroupsAssignedToVisitorGroup,
  getAllLocksAssignedDirectlyToVisitorGroup,
  getAmountOfMembersInGroup,
  getScheduleDaysForVisitorGroupGroup,
  getScheduleTimeForGroup,
  isOfficeModeEnabledForVisitorGroup,
  isScheduleSetUpForVisitorGroup
} from 'utils/VisitorGroup/VisitorGroup';
import { ColumnHeaderWithTooltip } from 'v2/components/ColumnHeaderWithTooltip/ColumnHeaderWithTooltip';
import DataGridSelectionToolbar from 'v2/components/DataGrid/DataGridSelectionToolbar';
import { getOverviewDataGridProps } from 'v2/components/DataGrid/getOverviewDataGridProps';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import { DataGridSelectionWithPagination } from 'v2/components/DataGrid/useDataGridSelectionWithPagination';
import DevicesAndGroupsCell from 'v2/components/DevicesAndGroupsCell/DevicesAndGroupsCell';
import { DevicesAndGroupsColumnHeader } from 'v2/components/DevicesAndGroupsColumnHeader/DevicesAndGroupsColumnHeader';
import { OriginCell } from 'v2/components/OriginCell/OriginCell';
import { OriginColumnHeader } from 'v2/components/OriginColumnHeader/OriginColumnHeader';
import StatusChip from 'v2/components/StatusChip/StatusChip';
import { TextCell, TwoLineCell } from 'v2/components/TextCell/TextCell';
import { WarningCell } from 'v2/components/Warning/Warning';
import { DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { getUserGroupPath } from 'v2/paths';
import { getVisitorGroupOriginLabel } from 'v2/utils/visitorGroupOrigin';

export interface GroupsOverviewRow {
  id: string;
  visitorGroup: VisitorGroup;
}

interface Props {
  selection: DataGridSelectionWithPagination<GroupsOverviewRow>;
  loading: boolean;
}

const GroupsOverviewTable: React.FC<Props> = ({ selection, loading }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));

  const headerWithId = renderHeaderWithId();

  const { gridRef, apiRef, rows, getGridProps, isCheckboxClick, toolbarProps } = selection;

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'name',
      headerName: translation.name,
      sortable: true,
      flex: 2,
      renderHeader: headerWithId,
      valueGetter: (_, row) => row.visitorGroup.name,
      renderCell: (params) => {
        const { visitorGroup } = params.row;
        return <TextCell variant="title">{visitorGroup.name}</TextCell>;
      }
    },
    {
      field: 'origin',
      headerName: translation.origin,
      sortable: true,
      width: 110,
      valueGetter: (_, row) => getVisitorGroupOriginLabel(row.visitorGroup, translation),
      renderHeader: renderHeaderWithId(undefined, () => <OriginColumnHeader variant="group" />),
      renderCell: (params) => {
        const { visitorGroup } = params.row;
        return <OriginCell label={getVisitorGroupOriginLabel(visitorGroup, translation)} />;
      }
    },
    {
      field: 'amount',
      headerName: translation.users,
      sortable: true,
      flex: 1,
      renderHeader: renderHeaderWithId(undefined, () => (
        <ColumnHeaderWithTooltip
          label={translation.users}
          tooltip={<Typography variant="caption">{translation.atlas_groups_max_users_tooltip}</Typography>}
        />
      )),
      valueGetter: (_, row) => getAmountOfMembersInGroup(row.visitorGroup),
      renderCell: (params) => {
        const { visitorGroup } = params.row;
        const amountOfMembersInGroup = getAmountOfMembersInGroup(visitorGroup);
        const maxNumberOfMembersInGroup = visitorGroup.maxNumberOfMembers;
        if (amountOfMembersInGroup)
          return maxNumberOfMembersInGroup ? (
            <TextCell>
              {amountOfMembersInGroup}/{maxNumberOfMembersInGroup}
            </TextCell>
          ) : (
            <TextCell>{amountOfMembersInGroup}</TextCell>
          );
        else {
          return <WarningCell label={translation.no_users} />;
        }
      }
    },
    {
      field: 'schedule',
      headerName: translation.schedule,
      sortable: false,
      minWidth: 130,
      flex: 1,
      renderHeader: renderHeaderWithId(undefined, () => (
        <ColumnHeaderWithTooltip
          label={translation.schedule}
          tooltip={<Typography variant="caption">{translation.schedule_column_tooltip}</Typography>}
        />
      )),
      renderCell: (params) => {
        const { visitorGroup } = params.row;
        if (isScheduleSetUpForVisitorGroup(visitorGroup)) {
          return (
            <TwoLineCell
              title={getScheduleDaysForVisitorGroupGroup(visitorGroup, translation)}
              content={getScheduleTimeForGroup(visitorGroup)}
            />
          );
        } else {
          return <WarningCell label={translation.no_schedule} />;
        }
      }
    },
    {
      field: 'device_groups',
      headerName: translation.device_groups_column_header,
      sortable: false,
      minWidth: 170,
      flex: 1,
      renderHeader: renderHeaderWithId(undefined, () => <DevicesAndGroupsColumnHeader />),
      renderCell: (params) => {
        const { visitorGroup } = params.row;
        const devicesCount = getAllLocksAssignedDirectlyToVisitorGroup(visitorGroup).length;
        const groupsCount = getAllLockGroupsAssignedToVisitorGroup(visitorGroup).length;
        return <DevicesAndGroupsCell devicesCount={devicesCount} groupsCount={groupsCount} />;
      }
    },
    {
      field: 'office_mode',
      headerName: translation.office_mode,
      sortable: true,
      minWidth: 160,
      renderHeader: renderHeaderWithId(undefined, () => (
        <ColumnHeaderWithTooltip
          label={translation.office_mode}
          tooltip={
            <Stack spacing={1}>
              <Typography variant="caption">{translation.office_mode_tooltip_description}</Typography>
              <Typography variant="caption">{translation.office_mode_tooltip_permission}</Typography>
            </Stack>
          }
        />
      )),
      valueGetter: (_, row) => isOfficeModeEnabledForVisitorGroup(row.visitorGroup),
      renderCell: (params) => {
        const { visitorGroup } = params.row;
        return (
          <TextCell>{isOfficeModeEnabledForVisitorGroup(visitorGroup) ? translation.yes : translation.no}</TextCell>
        );
      }
    },
    {
      field: 'access',
      headerName: translation.access,
      sortable: true,
      width: isExtraLargeScreen ? 140 : 120,
      renderHeader: renderHeaderWithId(undefined, () => (
        <ColumnHeaderWithTooltip
          label={translation.access}
          tooltip={<Typography variant="caption">{translation.access_column_tooltip}</Typography>}
        />
      )),
      valueGetter: (_, row) => getAccessStatusForVisitorGroup(row.visitorGroup),
      renderCell: (params) => {
        const { visitorGroup } = params.row;
        const complete = getAccessStatusForVisitorGroup(visitorGroup);
        return (
          <StatusChip
            color={complete ? 'success' : 'error'}
            icon={complete ? CheckOutlined : CloseOutlined}
            label={complete ? translation.complete : translation.incomplete}
            iconOnly={!isExtraLargeScreen}
          />
        );
      }
    }
  ];

  const columnVisibilityModel = {
    name: true,
    origin: isLargeScreen,
    amount: isMediumScreen,
    schedule: isLargeScreen,
    device_groups: isMediumScreen,
    office_mode: isExtraLargeScreen,
    access: true
  };

  const overviewDataGridProps = getOverviewDataGridProps({ rowCount: rows.length, loading });

  return (
    <>
      <DataGridSelectionToolbar {...toolbarProps} />
      <DataGrid
        {...overviewDataGridProps}
        {...getGridProps(columns)}
        ref={gridRef}
        apiRef={apiRef}
        rows={rows}
        columnVisibilityModel={columnVisibilityModel}
        rowHeight={DATAGRID_ROW_HEIGHT}
        initialState={{
          ...overviewDataGridProps.initialState,
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
        }}
        onRowClick={({ id }, event) => {
          if (isCheckboxClick(event)) return;
          navigate(getUserGroupPath(String(id)));
        }}
        disableColumnSelector
        disableColumnFilter
      />
    </>
  );
};

export default GroupsOverviewTable;
